import { wencesApi } from "@/api/wencesApi";
import { resolveAutoTrackingRuleFromConfig } from "@/utils/findTeuAutoTrackingSources";

let autoTrackingModelsCache = null;
let autoTrackingModelsLoadPromise = null;

async function loadAutoTrackingModels() {
  if (autoTrackingModelsCache) return autoTrackingModelsCache;
  if (!autoTrackingModelsLoadPromise) {
    autoTrackingModelsLoadPromise = wencesApi
      .get("/api/gotrading/findteu/config")
      .then((response) => {
        const cfg = response.data || {};
        autoTrackingModelsCache = Array.isArray(cfg.autoTrackingModels)
          ? cfg.autoTrackingModels
          : [];
        return autoTrackingModelsCache;
      })
      .catch(() => {
        autoTrackingModelsLoadPromise = null;
        autoTrackingModelsCache = null;
        return [];
      });
  }
  return autoTrackingModelsLoadPromise;
}

export const findTeuService = {
  invalidateAutoTrackingModelsCache() {
    autoTrackingModelsCache = null;
    autoTrackingModelsLoadPromise = null;
  },

  /** Regla del modelo desde la config cacheada (una sola carga por sesión; se invalida al editar el mapeo). */
  async getCachedAutoTrackingRuleForModel(modelName) {
    const mn = String(modelName || "").trim();
    if (!mn) return null;
    const list = await loadAutoTrackingModels();
    return list.find((r) => r && String(r.modelName).toLowerCase() === mn.toLowerCase()) || null;
  },

  async getAutoTrackingRuleForModel(modelName) {
    const mn = String(modelName || "").trim();
    if (!mn) return null;
    try {
      const cfg = await this.getConfig();
      return resolveAutoTrackingRuleFromConfig(cfg, mn);
    } catch (_) {
      const list = await loadAutoTrackingModels();
      return (
        list.find(
          (r) => r && String(r.modelName).toLowerCase() === mn.toLowerCase()
        ) || null
      );
    }
  },
  async getConfig() {
    try {
      const response = await wencesApi.get("/api/gotrading/findteu/config");
      return response.data;
    } catch (error) {
      console.error("Error getting FindTeu config:", error);
      throw error;
    }
  },

  async getScacList() {
    try {
      const response = await wencesApi.get("/api/gotrading/findteu/scac-list");
      return (response.data && response.data.data) ? response.data.data : [];
    } catch (error) {
      console.error("Error getting SCAC list:", error);
      return [];
    }
  },

  async updateConfig(partial) {
    try {
      const response = await wencesApi.patch("/api/gotrading/findteu/config", partial);
      return response.data;
    } catch (error) {
      console.error("Error updating FindTeu config:", error);
      throw error;
    }
  },

  async getReportItems(reportId, limit = 300) {
    try {
      const response = await wencesApi.get(
        `/api/gotrading/expanded/report/${reportId}`,
        { params: { limit } }
      );
      return response.data;
    } catch (error) {
      console.error("Error loading report items for FindTeu search:", error);
      throw error;
    }
  },

  async startTracking(trackingNumber, trackingType = 'booking', linkedItems = null, scac = null) {
    if (!trackingNumber || trackingNumber.trim() === "") {
      throw new Error("Tracking number is required");
    }

    try {
      const body = { trackingNumber, trackingType };
      if (linkedItems && Array.isArray(linkedItems) && linkedItems.length > 0) {
        body.linkedItems = linkedItems.map(li => ({
          modelName: li.modelName,
          itemId: typeof li.itemId === 'string' ? li.itemId : (li.itemId?._id ?? li.itemId)
        }));
      }
      if (scac != null && String(scac).trim()) body.scac = String(scac).trim();
      const response = await wencesApi.post(
        `/api/gotrading/findteu/tracking/start`,
        body
      );
      return response.data;
    } catch (error) {
      console.error("Error starting tracking:", error);
      throw error;
    }
  },

  async updateLinkedItems(trackingNumber, trackingType, linkedItems) {
    try {
      const response = await wencesApi.put(
        `/api/gotrading/findteu/tracking/${trackingType}/${encodeURIComponent(trackingNumber)}/linked-items`,
        { linkedItems: (linkedItems || []).map(li => ({
          modelName: li.modelName,
          itemId: typeof li.itemId === 'string' ? li.itemId : (li.itemId?._id ?? li.itemId)
        })) }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating linked items:", error);
      throw error;
    }
  },

  async getTrackingInfo(trackingNumber, trackingType = 'booking') {
    if (!trackingNumber || trackingNumber.trim() === "") {
      throw new Error("Tracking number is required");
    }

    try {
      const response = await wencesApi.get(
        `/api/gotrading/findteu/tracking/${trackingType}/${trackingNumber}`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting tracking info:", error);
      throw error;
    }
  },

  /** Refresca un tracking contra la API FindTeu sin esperar al cron diario. */
  async refreshTracking(trackingNumber, trackingType = 'booking') {
    if (!trackingNumber || String(trackingNumber).trim() === "") {
      throw new Error("Tracking number is required");
    }

    const response = await wencesApi.post(
      `/api/gotrading/findteu/tracking/${trackingType}/${encodeURIComponent(trackingNumber)}/refresh`
    );
    return response.data;
  },

  async getAllTrackings() {
    try {
      const response = await wencesApi.get(
        `/api/gotrading/findteu/trackings`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting all trackings:", error);
      throw error;
    }
  },

  async stopTracking(trackingNumber, trackingType = 'booking') {
    if (!trackingNumber || trackingNumber.trim() === "") {
      throw new Error("Tracking number is required");
    }

    try {
      const response = await wencesApi.post(
        `/api/gotrading/findteu/tracking/stop`,
        { trackingNumber, trackingType }
      );
      return response.data;
    } catch (error) {
      console.error("Error stopping tracking:", error);
      throw error;
    }
  },

  async stopAllTrackings() {
    try {
      const response = await wencesApi.post(
        `/api/gotrading/findteu/tracking/stop-all`
      );
      return response.data;
    } catch (error) {
      console.error("Error stopping all trackings:", error);
      throw error;
    }
  },

  async discardPendingChanges(trackingNumber, trackingType = 'booking', discards = []) {
    if (!trackingNumber || trackingNumber.trim() === "") {
      throw new Error("Tracking number is required");
    }
    const response = await wencesApi.post(
      `/api/gotrading/findteu/tracking/discard-pending`,
      { trackingNumber, trackingType, discards }
    );
    return response.data;
  },

  /**
   * Apply tracking result mapping and update linked item using the same logic as JSON import.
   * Backend uses JSONImportController.applyMapping when available, then dynamicModelController.update.
   * Optional trackingNumber, trackingType, itemLabel are used to record this update in the tracking's mappingUpdateHistory.
   */
  async applyAutoTrackingModelMapping(modelName, payload = {}) {
    const body =
      typeof payload === "object" && payload !== null && !Array.isArray(payload)
        ? { modelName, ...payload }
        : {
            modelName,
            defaultTrackingType: arguments[1] || "container",
            trackingNumberField: arguments[2] || "",
            carrierScacField: arguments[3] || "",
          };
    const response = await wencesApi.post("/api/gotrading/findteu/auto-tracking-models", body);
    findTeuService.invalidateAutoTrackingModelsCache();
    return response.data;
  },

  async removeAutoTrackingModelMapping(modelName) {
    const response = await wencesApi.delete(
      `/api/gotrading/findteu/auto-tracking-models/${encodeURIComponent(modelName)}`
    );
    findTeuService.invalidateAutoTrackingModelsCache();
    return response.data;
  },

  async patchAutoTrackingModelMapping(modelName, payload) {
    const response = await wencesApi.patch("/api/gotrading/findteu/auto-tracking-models", {
      modelName,
      ...payload,
    });
    findTeuService.invalidateAutoTrackingModelsCache();
    return response.data;
  },

  /** Para el popup del modo «Preguntar»: candidatos del ítem y si alguno ya tiene tracking activo. */
  async getAutoTrackingItemStatus(modelName, itemId) {
    const response = await wencesApi.post("/api/gotrading/findteu/auto-tracking/item-status", {
      modelName,
      itemId,
    });
    return response.data;
  },

  async startAutoTrackingForItem(modelName, itemId) {
    const response = await wencesApi.post("/api/gotrading/findteu/auto-tracking/start-item", {
      modelName,
      itemId,
    });
    return response.data;
  },

  /** SCAC que se enviaría por ítem (resuelve referencias en el back). Solo informativo. */
  async previewAutoTrackingScac(modelName, itemIds) {
    const response = await wencesApi.post("/api/gotrading/findteu/auto-tracking/scac-preview", {
      modelName,
      itemIds,
    });
    return response.data;
  },

  async applyTrackingUpdate(modelName, itemId, trackingData, mapping, { trackingNumber, trackingType, itemLabel } = {}) {
    try {
      const body = { modelName, itemId, trackingData, mapping };
      if (trackingNumber) body.trackingNumber = trackingNumber;
      if (trackingType) body.trackingType = trackingType;
      if (itemLabel != null) body.itemLabel = String(itemLabel);
      const response = await wencesApi.post(
        "/api/gotrading/findteu/apply-tracking-update",
        body
      );
      return response.data;
    } catch (error) {
      console.error("Error applying tracking update:", error);
      throw error;
    }
  }
};

